import React, { Fragment, useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  Box,
  InputLabel,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Grid,
  Select,
  MenuItem,
  FormControl,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  CustomTextField,
  TypographyLarge,
} from "../../../../components/customComponent";

import { useDispatch, useSelector } from "react-redux";
import { createDisruption } from "../../../store/actions/smrt/disruptionAction";
import dayjs from "dayjs";
import PreviewDisruption from "./preview/PreviewDisruption";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import {
  getDirectionImage,
  getDisruptionAssetSrc,
} from "../helper/disruptionHelper";
import VerticalPreview from "./preview/VerticalPreview";

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const CreateTrainDisruption = () => {
  const { t } = useTranslation("smrtDisruption");
  const { t: tCommon } = useTranslation();
  const dispatch = useDispatch();

  const { features } = useSelector((state) => state.auth);
  const { lines: stateLines } = useSelector((state) => state.smrtDisruption);
  const [open, setOpen] = useState(false);

  const [form, setForm] = useState({
    type: "disruption",
    dir: "",
    delayMinutes: 10,
    resumeTime: "",
    lineId: "",
    fromId: "",
    toId: "",
    isBothDirection: false,
    isMessageHidden: true,
    isAffectingAllStation: false,
  });

  const [affectedStations, setAffectedStations] = useState([]);

  const [lines, setLines] = useState([]);
  const [stations, setStations] = useState([]);

  const [previewDisabled, setPreviewDisabled] = useState(true);

  const delayList = [10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];

  const directionMap = {
    asc: {
      img: getDisruptionAssetSrc(getDirectionImage("asc")),
      label: t("createDisruption.form.directionAsc"),
    },
    desc: {
      img: getDisruptionAssetSrc(getDirectionImage("desc")),
      label: t("createDisruption.form.directionDesc"),
    },
    both: {
      img: getDisruptionAssetSrc(getDirectionImage("both")),
      label: t("createDisruption.form.directionBoth"),
    },
    sector: {
      img: getDisruptionAssetSrc(getDirectionImage("sector")),
      label: t("createDisruption.form.directionSector"),
    },
  };

  const setResumeTime = () => {
    const newFormat = dayjs().format("YYYY-MM-DDTHH:mm");
    setForm({ ...form, resumeTime: newFormat });
  };

  const handleClose = () => {
    setForm({
      type: "disruption",
      dir: "",
      delayMinutes: 10,
      resumeTime: "",
      lineId: "",
      fromId: "",
      toId: "",
      isBothDirection: false,
      isMessageHidden: true,
    });

    setPreviewDisabled(true);
    setOpen(false);
  };

  const handleChange = (event) => {
    let extraData = {};

    if (event.target.name === "lineId") {
      const listStation = lines.find(
        (line) => line.id === parseInt(event.target.value),
      ).stations;
      const sorted = [...listStation].sort((a, b) =>
        a.abbreviation.localeCompare(b.abbreviation),
      );

      setAffectedStations([]);
      setStations(sorted);
      setPreviewDisabled(true);
    }

    if (event.target.name === "type") {
      event.target.value === "disruption"
        ? (extraData = { isMessageHidden: true })
        : (extraData = { isMessageHidden: false });

      if (event.target.value === "station-closure" && !isEmpty(form.lineId)) {
        extraData = {
          fromId: stations[0]?.id,
          toId: stations[stations.length - 1]?.id,
        };
      }
    }
    setForm({ ...form, [event.target.name]: event.target.value, ...extraData });
    if (event.target.name === "fromId" || event.target.name === "toId") {
      setAffectedStations([]);

      if (form.fromId || form.toId) setPreviewDisabled(false);
    }
  };

  const handleChangeStationCheckbox = (event, isChecked) => {
    const value = parseInt(event.target.value);
    const stationObject = stations.find((station) => station.id === value);
    if (isChecked) {
      setAffectedStations([...affectedStations, { id: stationObject.id }]);
    }
    if (!isChecked) {
      setAffectedStations(
        affectedStations.filter((item) => item.id !== stationObject.id),
      );
    }
    setPreviewDisabled(false);
  };

  const handleChangeHideMessageCheckbox = (event, isChecked) => {
    setForm({ ...form, isMessageHidden: isChecked });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const newDateFormat = new Date(form.resumeTime).toISOString();

    const payload = {
      type: form.type,
      delayMinutes: form.delayMinutes,
      resumeAt: newDateFormat,
      lineId: parseInt(form.lineId),
      fromStationId:
        form.type === "station-closure"
          ? parseInt(affectedStations[0].id)
          : parseInt(form.fromId),
      toStationId:
        form.type === "station-closure"
          ? parseInt(affectedStations[0].id)
          : parseInt(form.toId),
      isMessageHidden: form.isMessageHidden,
      affectedStations: affectedStations,
      direction: form.type === "station-closure" ? "asc" : form.dir,
      isAffectingAllStation: form.isAffectingAllStation,
    };

    dispatch(createDisruption(payload));
    resetForm();
    setOpen(false);
  };

  const resetForm = () => {
    setForm({
      type: "disruption",
      dir: "",
      delayMinutes: 10,
      resumeTime: "",
      lineId: "",
      fromId: "",
      toId: "",
      isBothDirection: false,
      isMessageHidden: true,
    });
    setAffectedStations([]);
    setPreviewDisabled(true);
  };

  const getPreviewData = () => {
    const newDateFormat = new Date(form.resumeTime).toISOString();

    let newAffected = [];
    affectedStations.forEach((station) => {
      newAffected.push(stations.filter((obj) => obj.id === station.id)[0]);
    });

    let previewData = [
      {
        type: form.type,
        delayMinutes: form.delayMinutes,
        resumeAt: newDateFormat,
        line: lines.filter((obj) => obj.id === parseInt(form.lineId))[0],
        fromStation: stations.filter(
          (obj) => obj.id === parseInt(form.fromId),
        )[0],
        toStation: stations.filter((obj) => obj.id === parseInt(form.toId))[0],
        isMessageHidden: form.isMessageHidden,
        affectedStations: newAffected,
        direction: form.dir,
        isAffectingAllStation: form.isAffectingAllStation,
      },
    ];

    return previewData;
  };

  useEffect(() => {
    if (open) {
      setLines([...stateLines]);
      setResumeTime();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        startIcon={<AddIcon fontSize="small" />}
        variant="contained"
        fullWidth
        sx={{ boxShadow: 3 }}
      >
        {t("createDisruption.mainBtn")}
      </Button>

      <Dialog
        open={open}
        maxWidth="lg"
        fullWidth
        onClose={() => setOpen(false)}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            {t("createDisruption.title")}
            <IconButton onClick={handleClose}>
              <CloseIcon fontSize="small" color="inherit" />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Box>
              <StyledBox>
                <InputLabel>{t("createDisruption.form.typeLabel")}</InputLabel>
                <CustomTextField
                  autoFocus
                  required
                  select
                  SelectProps={{ native: true }}
                  fullWidth
                  margin="dense"
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    {t("createDisruption.form.typeDefault")}
                  </option>
                  <option value="disruption">
                    {t("createDisruption.form.typeDisruption")}
                  </option>
                  {features?.["SMRT_SRG"] === false ? (
                    <>
                      <option value="delay">
                        {t("createDisruption.form.TypeDelay")}
                      </option>
                      <option value="station-closure">
                        {t("createDisruption.form.TypeClosure")}
                      </option>
                    </>
                  ) : null}
                </CustomTextField>
              </StyledBox>

              <StyledBox>
                <InputLabel>{t("createDisruption.form.lineLabel")}</InputLabel>
                <CustomTextField
                  select
                  required
                  SelectProps={{ native: true }}
                  fullWidth
                  margin="dense"
                  name="lineId"
                  value={form.lineId}
                  onChange={handleChange}
                >
                  <option value="" disabled>
                    {t("createDisruption.form.lineDefault")}
                  </option>
                  {lines.map((line) => (
                    <option value={line.id} key={line.id}>
                      {line.name}
                    </option>
                  ))}
                </CustomTextField>
              </StyledBox>

              {form.type !== "station-closure" && (
                <>
                  <StyledBox>
                    <InputLabel id="direction-label">
                      {t("createDisruption.form.directionLabel")}
                    </InputLabel>
                    <FormControl fullWidth>
                      <Select
                        labelId="direction-label"
                        name="dir"
                        value={form.dir}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <>{t("createDisruption.form.directionDefault")}</>
                            );
                          }

                          const opt = directionMap[selected];
                          return (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <img
                                src={opt.img}
                                alt={opt.label}
                                width={30}
                                height={30}
                              />
                              {opt.label}
                            </Box>
                          );
                        }}
                      >
                        <MenuItem value="">
                          <ListItemText
                            primary={t(
                              "createDisruption.form.directionDefault",
                            )}
                          />
                        </MenuItem>

                        {Object.entries(directionMap).map(
                          ([key, { label }]) => (
                            <MenuItem value={key}>
                              <ListItemIcon sx={{ minWidth: 50, mr: 2 }}>
                                <img
                                  src={getDisruptionAssetSrc(
                                    getDirectionImage(key),
                                  )}
                                  alt={label}
                                  width={40}
                                  height={40}
                                />
                              </ListItemIcon>
                              <ListItemText primary={label} />
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                  </StyledBox>

                  <StyledBox>
                    <InputLabel>
                      {t("createDisruption.form.fromStationLabel")}
                    </InputLabel>
                    <CustomTextField
                      select
                      required
                      SelectProps={{ native: true }}
                      fullWidth
                      margin="dense"
                      name="fromId"
                      value={form.fromId}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        {t("createDisruption.form.fromStationDefault")}
                      </option>
                      {stations.map((station) => (
                        <option value={station.id} key={"from" + station.id}>
                          {station.abbreviation + " - " + station.name}
                        </option>
                      ))}
                    </CustomTextField>
                  </StyledBox>

                  <StyledBox>
                    <InputLabel>
                      {t("createDisruption.form.toStationLabel")}
                    </InputLabel>
                    <CustomTextField
                      select
                      required
                      SelectProps={{ native: true }}
                      fullWidth
                      margin="dense"
                      name="toId"
                      value={form.toId}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        {t("createDisruption.form.toStationDefault")}
                      </option>
                      {stations.sort().map((station) => (
                        <option value={station.id} key={"to" + station.id}>
                          {station.abbreviation + " - " + station.name}
                        </option>
                      ))}
                    </CustomTextField>
                  </StyledBox>
                </>
              )}

              {form.type === "station-closure" && (
                <StyledBox>
                  <InputLabel>
                    {t("createDisruption.form.affectedStationLabel")}
                  </InputLabel>
                  <Box
                    sx={{ border: "1px solid #e7e7e7", borderRadius: "4px" }}
                  >
                    <Grid
                      container
                      sx={{ p: 1, maxHeight: 300, overflow: "auto" }}
                    >
                      {stations.map((station) => (
                        <Grid item key={station.id} xs={3} md={4}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                value={station.id}
                                key={station.id}
                                onChange={handleChangeStationCheckbox}
                              />
                            }
                            label={station.abbreviation + " - " + station.name}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </StyledBox>
              )}

              {form.type === "disruption" &&
                !form.isMessageHidden &&
                !features?.["SMRT_SRG"] && (
                  <StyledBox>
                    <InputLabel>
                      {t("createDisruption.form.resumeLabel")}
                    </InputLabel>
                    <CustomTextField
                      required
                      variant="outlined"
                      name="resumeTime"
                      margin="dense"
                      type="datetime-local"
                      value={form.resumeTime}
                      onChange={handleChange}
                    />
                  </StyledBox>
                )}

              {form.type === "delay" && !form.isMessageHidden && (
                <StyledBox>
                  <InputLabel>
                    {t("createDisruption.form.delayLabel")}
                  </InputLabel>
                  <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                    <Select
                      size="small"
                      value={form.delayMinutes}
                      name="delayMinutes"
                      onChange={handleChange}
                    >
                      {delayList.map((item) => (
                        <MenuItem value={item} key={item}>
                          {item.toString()}
                        </MenuItem>
                      ))}
                    </Select>
                    <TypographyLarge sx={{ ml: 1 }}>
                      {t("createDisruption.form.delayMinuteText")}
                    </TypographyLarge>
                  </Box>
                </StyledBox>
              )}

              {form.type !== "station-closure" && !features?.["SMRT_SRG"] && (
                <StyledBox>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="hideMessage"
                        onChange={handleChangeHideMessageCheckbox}
                        checked={form.isMessageHidden}
                      />
                    }
                    label={t("createDisruption.form.hideMessageLabel")}
                  />
                </StyledBox>
              )}

              <StyledBox>
                {features?.["SMRT_SRG"] ? (
                  <VerticalPreview
                    fullWidth={false}
                    previewType={"CREATE"}
                    disabled={previewDisabled}
                    createData={getPreviewData}
                  />
                ) : (
                  <PreviewDisruption
                    fullWidth={false}
                    previewType={"CREATE"}
                    disabled={previewDisabled}
                    createData={getPreviewData}
                  />
                )}
              </StyledBox>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button fullWidth variant="outlined" onClick={handleClose}>
              {tCommon("cancelBtn")}
            </Button>
            <Button fullWidth variant="contained" type="submit">
              {t("createDisruption.form.submitBtn")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default CreateTrainDisruption;
